import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Loader,
  Dimmer,
  Segment,
  Table,
  Button,
  Message,
  Form,
  Radio,
  Input,
} from "semantic-ui-react";
import axios from "../../../../../../apis/dvdlocator";

// Import components
import InventorySection from "./InventorySection";
import CreateInventory from "./CreateInventory/CreateInventory";
import SplitInventoryWizard from "./SplitInventoryWizard";
import { SupplyUnitSearch } from "../../../../../search/SupplyUnitSearch";

const InventoryDetails = ({ open, onClose, supplyUnit }) => {
  const inventory_id = supplyUnit.inventory.inventory_id;

  const [isLoading, setIsLoading] = useState(true);
  const [splittingInventory, setSplittingInventory] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [supplyUnits, setSupplyUnits] = useState([]);
  const [title, setTitle] = useState("");
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [addingEquivalents, setAddingEquivalents] = useState(false);

  // New state for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingSupplyUnits, setPendingSupplyUnits] = useState([]);
  const [confirmationError, setConfirmationError] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [containsInvalidParts, setContainsInvalidParts] = useState(false);

  // State for shelf selection
  const [containsMismatchedShelves, setContainsMismatchedShelves] =
    useState(false);
  const [availableShelves, setAvailableShelves] = useState([]);
  const [selectedShelf, setSelectedShelf] = useState(null);
  const [customShelf, setCustomShelf] = useState("");

  // Use useCallback to memoize the function so it doesn't change on every render
  const loadInventoryData = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await axios.get(`/supply?inventoryId=${inventory_id}`);

      const inventory = {
        inventory_title: res.data[0].inventory_title,
        inventory_id: res.data[0].inventory_id,
        inv: res.data[0].inv,
        inventory_shelf: res.data[0].inventory_shelf,
        part_number: res.data[0].part_number,
      };

      // Format supply units to match our component's expected structure
      const formattedSupplyUnits = res.data.map((unit) => ({
        upc: unit.upc,
        supply_title: unit.supply_title,
        inv: "",
        assign_new_inventory: unit.assign_new_inventory || false,
        new_inventory_title: unit.new_inventory_title || false,
        new_inventory_shelf: unit.new_inventory_shelf || false,
        new_inventory_id: null, // For grouping in the GROUP mode
      }));

      return { inventory, formattedSupplyUnits };
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      throw error;
    }
  }, [inventory_id]); // Only recreate if inventory_id changes

  useEffect(() => {
    if (open && inventory_id) {
      const fetchData = async () => {
        try {
          const { inventory, formattedSupplyUnits } = await loadInventoryData();

          setInventory(inventory);
          setTitle(inventory.inventory_title);
          setSupplyUnits(formattedSupplyUnits);
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [open, inventory_id, loadInventoryData]); // Now includes loadInventoryData

  const handleWizardComplete = async () => {
    try {
      const { inventory, formattedSupplyUnits } = await loadInventoryData();

      setInventory(inventory);
      setTitle(inventory.inventory_title);
      setSupplyUnits(formattedSupplyUnits);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleTitleUpdate = (newTitle) => {
    const updatedInventory = { ...inventory, inventoryTitle: newTitle };
    setInventory(updatedInventory);
  };

  const startSplittingInventory = () => {
    setSplittingInventory(true);
  };

  const toggleAddingEquivalents = () => {
    setAddingEquivalents(!addingEquivalents);
  };

  // Modified to show confirmation modal instead of directly adding
  const handleSupplyUnitsAdd = (newSupplyUnits) => {
    const invalidParts = newSupplyUnits.some(
      (unit) => unit.part_number !== inventory.part_number
    );

    // Check for mismatched shelf values
    const inventoryShelf = inventory.inventory_shelf || "";
    const hasMismatchedShelves = newSupplyUnits.some(
      (unit) => (unit.inventory_shelf || "") !== inventoryShelf
    );

    // Pool unique shelf values from inventory and supply units
    const shelfSet = new Set();
    if (inventoryShelf) shelfSet.add(inventoryShelf);
    newSupplyUnits.forEach((unit) => {
      if (unit.inventory_shelf) shelfSet.add(unit.inventory_shelf);
    });
    const uniqueShelves = Array.from(shelfSet);

    setPendingSupplyUnits(newSupplyUnits);
    setContainsInvalidParts(invalidParts);
    setContainsMismatchedShelves(hasMismatchedShelves);
    setAvailableShelves(uniqueShelves);
    setSelectedShelf(hasMismatchedShelves ? null : inventoryShelf);
    setCustomShelf("");
    setConfirmationError(null); // Clear any previous errors
    setShowConfirmationModal(true);
  };

  // New function to handle confirmation
  const handleConfirmAddSupplyUnits = async () => {
    setIsConfirming(true);
    setConfirmationError(null);

    const newInventoryQuantity =
      (pendingSupplyUnits[0].inv || 0) + (inventory.inv || 0);

    // Determine the shelf value to use
    const shelfValue = selectedShelf === "other" ? customShelf : selectedShelf;

    const requestBody = pendingSupplyUnits.map((unit) => ({
      retain_product_associations: true,
      previous_inventory_id: unit.inventory_id,
      inventory_id: inventory.inventory_id,
      upc: unit.upc,
      inv: newInventoryQuantity,
      ...(containsMismatchedShelves && shelfValue ? { shelf: shelfValue } : {}),
    }));

    try {
      await axios.post("/supply/link-inventory", { supplyUnits: requestBody });

      // Success - update the UI
      setSupplyUnits((prev) => [...prev, ...pendingSupplyUnits]);

      // Close the confirmation modal and reset state
      setShowConfirmationModal(false);
      setPendingSupplyUnits([]);
      setContainsInvalidParts(false);
      setContainsMismatchedShelves(false);
      setAvailableShelves([]);
      setSelectedShelf(null);
      setCustomShelf("");
      setAddingEquivalents(false);
      setInventory((prev) => ({
        ...prev,
        inv: newInventoryQuantity,
        ...(containsMismatchedShelves && shelfValue
          ? { inventory_shelf: shelfValue }
          : {}),
      }));
    } catch (err) {
      console.log(err);
      // Set error message for display
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to add supply units. Please try again.";
      setConfirmationError(errorMessage);
    } finally {
      setIsConfirming(false);
    }
  };

  // New function to handle cancellation
  const handleCancelAddSupplyUnits = () => {
    setShowConfirmationModal(false);
    setPendingSupplyUnits([]);
    setConfirmationError(null);
    setContainsInvalidParts(false);
    setContainsMismatchedShelves(false);
    setAvailableShelves([]);
    setSelectedShelf(null);
    setCustomShelf("");
    setIsConfirming(false);
  };

  if (!inventory_id) {
    return <CreateInventory onClose={onClose} upc={supplyUnit.supply.upc} />;
  }

  if (isLoading) {
    return (
      <Modal open={open} onClose={onClose} closeOnDimmerClick size="large">
        <Modal.Header>Inventory ID Details</Modal.Header>
        <Modal.Content>
          <Segment style={{ height: "200px" }}>
            <Dimmer active inverted>
              <Loader inverted>Loading Inventory Data</Loader>
            </Dimmer>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose} disabled={isLoading}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }

  return (
    <>
      <Modal open={open} onClose={onClose} closeOnDimmerClick size="large">
        <Modal.Header>
          Inventory ID Details
          <div
            style={{
              fontSize: "0.7em",
              marginTop: "5px",
              fontWeight: "normal",
            }}
          >
            Below are all supply units linked to this inventory record
          </div>
        </Modal.Header>
        <Modal.Content>
          {/* Inventory title and info section */}
          <InventorySection
            inventory={inventory}
            setInventory={setInventory}
            title={title}
            setTitle={setTitle}
            isTitleEditing={isTitleEditing}
            setIsTitleEditing={setIsTitleEditing}
            supplyUnits={supplyUnits}
            startWizard={startSplittingInventory}
            onTitleUpdate={handleTitleUpdate}
          />

          {/* Default inventory view */}
          <Segment>
            {/* Supply units table */}
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>UPC</Table.HeaderCell>
                  <Table.HeaderCell>Title</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {supplyUnits.map((unit, index) => (
                  <Table.Row key={`${unit.upc}-${index}`}>
                    <Table.Cell>{unit.upc}</Table.Cell>
                    <Table.Cell>{unit.supply_title}</Table.Cell>
                  </Table.Row>
                ))}
                <Table.Row
                  key="add-equivalent-row"
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Table.Cell
                    colSpan="2"
                    style={{
                      padding: addingEquivalents ? "10px" : "20px",
                      backgroundColor: "transparent",
                    }}
                  >
                    {addingEquivalents ? (
                      <div>
                        <div
                          style={{
                            marginBottom: "10px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ fontSize: "0.9em", color: "#666" }}>
                            Search for supply units to add to this inventory:
                          </span>
                          <Button
                            size="mini"
                            onClick={toggleAddingEquivalents}
                            style={{ marginLeft: "10px" }}
                          >
                            Cancel
                          </Button>
                        </div>
                        <SupplyUnitSearch
                          existingSupplyUnits={supplyUnits}
                          onSupplyUnitsAdd={handleSupplyUnitsAdd}
                          placeholder="Search by UPC or Title"
                          minCharacters={2}
                          className="scrolling-search"
                          fluid
                        />
                      </div>
                    ) : (
                      <div style={{ textAlign: "center" }}>
                        <Button
                          basic
                          color="blue"
                          icon="plus"
                          content="Add Equivalent"
                          onClick={toggleAddingEquivalents}
                          style={{
                            borderRadius: "4px",
                            fontWeight: "500",
                          }}
                        />
                      </div>
                    )}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose} disabled={isLoading}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Inventory Splitting Wizard */}
      <SplitInventoryWizard
        open={splittingInventory}
        onClose={() => setSplittingInventory(false)}
        inventory={inventory}
        supplyUnits={supplyUnits}
        setSupplyUnits={setSupplyUnits}
        onComplete={handleWizardComplete}
        isLoading={isLoading}
      />

      {/* Confirmation Modal for Adding Supply Units */}
      <Modal
        open={showConfirmationModal}
        onClose={handleCancelAddSupplyUnits}
        size="small"
      >
        <Modal.Header>Confirm Add Supply Units</Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to add the following supply units as
            equivalents?
          </p>

          {containsInvalidParts && (
            <Message negative>
              <Message.Header>Invalid Parts Detected</Message.Header>
              <p>
                Some of the selected supply units have different part numbers
                than the current inventory. These cannot be added as
                equivalents.
              </p>
            </Message>
          )}

          {confirmationError && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{confirmationError}</p>
            </Message>
          )}

          <Table celled style={{ marginTop: "15px" }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>UPC</Table.HeaderCell>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell>Part Number</Table.HeaderCell>
                <Table.HeaderCell>Shelf</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pendingSupplyUnits.map((unit, index) => (
                <Table.Row key={`pending-${unit.upc}-${index}`}>
                  <Table.Cell>{unit.upc}</Table.Cell>
                  <Table.Cell>{unit.supply_title}</Table.Cell>
                  <Table.Cell>{unit.part_number}</Table.Cell>
                  <Table.Cell>{unit.inventory_shelf || "-"}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {containsMismatchedShelves && (
            <Segment style={{ marginTop: "15px" }}>
              <Message warning>
                <Message.Header>Shelf Location Mismatch</Message.Header>
                <p>
                  The selected supply units have different shelf locations than
                  the current inventory (shelf:{" "}
                  {inventory.inventory_shelf || "none"}). Please select which
                  shelf location to use for all items.
                </p>
              </Message>
              <Form>
                <Form.Field>
                  <label>Select Shelf Location</label>
                </Form.Field>
                {availableShelves.map((shelf) => (
                  <Form.Field key={shelf}>
                    <Radio
                      label={shelf}
                      name="shelfSelection"
                      value={shelf}
                      checked={selectedShelf === shelf}
                      onChange={() => setSelectedShelf(shelf)}
                    />
                  </Form.Field>
                ))}
                <Form.Field>
                  <Radio
                    label="Other (enter custom shelf)"
                    name="shelfSelection"
                    value="other"
                    checked={selectedShelf === "other"}
                    onChange={() => setSelectedShelf("other")}
                  />
                </Form.Field>
                {selectedShelf === "other" && (
                  <Form.Field style={{ marginLeft: "25px" }}>
                    <Input
                      placeholder="Enter shelf location"
                      value={customShelf}
                      onChange={(e) => setCustomShelf(e.target.value)}
                    />
                  </Form.Field>
                )}
              </Form>
            </Segment>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelAddSupplyUnits} disabled={isConfirming}>
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={handleConfirmAddSupplyUnits}
            loading={isConfirming}
            disabled={
              isConfirming ||
              containsInvalidParts ||
              (containsMismatchedShelves &&
                (!selectedShelf ||
                  (selectedShelf === "other" && !customShelf.trim())))
            }
          >
            Add Supply Units
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default InventoryDetails;
